import React, { useState, useRef, useCallback } from 'react';
import { FileIcon } from '../../assets/icons';

const FileUpload = ({
  onFileSelect,
  onTextExtracted,
  acceptedTypes = ['.pdf', '.doc', '.docx', '.txt'],
  maxSize = 10 * 1024 * 1024, 
  className = '',
  disabled = false,
  ...props
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [disabled]);

  // Extract text from file based on type
  const extractTextFromFile = async (file) => {
    try {
      let extractedText = '';

      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        extractedText = await extractTextFromTxt(file);
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        extractedText = await extractTextFromPdf(file);
      } else if (file.name.endsWith('.docx')) {
        extractedText = await extractTextFromDocx(file);
      } else if (file.name.endsWith('.doc')) {
        throw new Error('DOC files are not supported. Please convert to DOCX or PDF.');
      } else {
        throw new Error('Unsupported file type for text extraction');
      }

      if (onTextExtracted) {
        onTextExtracted(extractedText);
      }

      return extractedText;
    } catch (error) {
      console.error('Text extraction failed:', error);
      throw error;
    }
  };

  // Extract text from TXT files
  const extractTextFromTxt = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Failed to read text file'));
      reader.readAsText(file);
    });
  };

  // Extract text from PDF files
  const extractTextFromPdf = async (file) => {
    try {
      if (typeof window.pdfjsLib === 'undefined') {
        await loadPdfJs();
      }

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        setProgress((i / pdf.numPages) * 100);
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText.trim();
    } catch (error) {
      throw new Error('Failed to extract text from PDF. Ensure the PDF contains selectable text.');
    }
  };

  // Extract text from DOCX files
  const extractTextFromDocx = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const content = new TextDecoder('utf-8').decode(uint8Array);
      
      // Extract text content from DOCX XML structure
      const textMatches = content.match(/<w:t[^>]*>([^<]+)<\/w:t>/g);
      if (textMatches) {
        const text = textMatches
          .map(match => match.replace(/<w:t[^>]*>([^<]+)<\/w:t>/, '$1'))
          .join(' ');
        return text.trim();
      }
      
      throw new Error('Could not extract text from DOCX file.');
    } catch (error) {
      throw new Error('Failed to extract text from DOCX file. Try converting to PDF or TXT.');
    }
  };

  // Load PDF.js library
  const loadPdfJs = () => {
    return new Promise((resolve, reject) => {
      if (window.pdfjsLib) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load PDF.js library'));
      document.head.appendChild(script);
    });
  };

  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      alert(`Invalid file type. Please upload: ${acceptedTypes.join(', ')}`);
      return;
    }

    if (file.size > maxSize) {
      alert(`File size too large. Maximum size: ${(maxSize / (1024 * 1024)).toFixed(1)}MB`);
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    
    try {
      await extractTextFromFile(file);
      onFileSelect(file);
    } catch (error) {
      alert(`Failed to process file: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  }, [acceptedTypes, maxSize, onFileSelect, onTextExtracted]);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const openFileDialog = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const getAcceptedTypesString = () => {
    return acceptedTypes.map(type => type.replace('.', '')).join(', ').toUpperCase();
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`
          relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300
          ${isDragOver 
            ? 'border-red-500 bg-red-50 scale-105' 
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
          {...props}
        />
        
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className={`
            p-4 rounded-full transition-all duration-300
            ${isDragOver ? 'bg-red-100 scale-110' : 'bg-gray-100'}
          `}>
            <FileIcon width="48px" height="48px" className="text-gray-600" />
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {isDragOver ? 'Drop your file here' : 'Upload your file'}
            </h3>
            <p className="text-sm text-gray-600">
              Drag and drop your file here, or{' '}
              <span className="text-red-600 font-medium hover:text-red-700">
                browse files
              </span>
            </p>
            <p className="text-xs text-gray-500">
              Supported formats: {getAcceptedTypesString()} (Max: {(maxSize / (1024 * 1024)).toFixed(1)}MB)
            </p>
          </div>
        </div>

        {/* Processing Overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  {progress > 0 ? 'Extracting text...' : 'Processing file...'}
                </p>
                {progress > 0 && (
                  <div className="w-48 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                )}
                {progress > 0 && (
                  <p className="text-xs text-gray-500">{Math.round(progress)}%</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
